<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Intune"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      :showSearch="false"
      :showExport="false"
      @refresh="loadData"
    />

    <div class="page-body">
      <div v-if="error" class="error-banner">{{ error }}</div>

      <div class="tabs-bar">
        <button v-for="tab in tabs" :key="tab.key" class="tab-btn" :class="{ 'tab-active': activeTab === tab.key }" @click="activeTab = tab.key">
          {{ tab.label }}
          <span class="tab-count" v-if="tabCount(tab.key) >= 0">{{ tabCount(tab.key) }}</span>
        </button>
      </div>

      <div v-if="tableState === 'loading'" class="loading-block">Loading Intune data...</div>

      <div v-else-if="activeTab === 'devices'" class="tab-content">
        <div v-if="data.devices.length === 0" class="empty-block">No managed devices found in this tenant.</div>
        <table v-else class="data-table">
          <thead><tr><th>Device</th><th>OS</th><th>Compliance</th><th>Last Sync</th></tr></thead>
          <tbody>
            <tr v-for="d in data.devices" :key="d.deviceId">
              <td class="cell-name">{{ d.deviceName || d.deviceId }}</td>
              <td>{{ d.osVersion || '--' }}</td>
              <td><span class="pill" :class="d.complianceState === 'compliant' ? 'pill-ok' : 'pill-warn'">{{ d.complianceState || 'unknown' }}</span></td>
              <td class="date-cell">{{ formatDate(d.lastSyncDateTime) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="activeTab === 'compliance'" class="tab-content">
        <div v-if="data.compliancePolicies.length === 0" class="empty-block">No compliance policies configured.</div>
        <table v-else class="data-table">
          <thead><tr><th>Policy</th><th>Platform</th><th>Created</th><th>Modified</th></tr></thead>
          <tbody>
            <tr v-for="p in data.compliancePolicies" :key="p.policyId" class="cursor-pointer" @click="openDetail('Compliance Policy', p)">
              <td class="cell-name">{{ p.displayName }}</td>
              <td>{{ p.platform || '--' }}</td>
              <td class="date-cell">{{ formatDate(p.createdDateTime) }}</td>
              <td class="date-cell">{{ formatDate(p.lastModifiedDateTime) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="activeTab === 'configs'" class="tab-content">
        <div v-if="data.deviceConfigs.length === 0" class="empty-block">No device configuration profiles found.</div>
        <table v-else class="data-table">
          <thead><tr><th>Configuration</th><th>Platform</th><th>Created</th><th>Modified</th></tr></thead>
          <tbody>
            <tr v-for="c in data.deviceConfigs" :key="c.configId" class="cursor-pointer" @click="openDetail('Device Configuration', c)">
              <td class="cell-name">{{ c.displayName }}</td>
              <td>{{ c.platform || '--' }}</td>
              <td class="date-cell">{{ formatDate(c.createdDateTime) }}</td>
              <td class="date-cell">{{ formatDate(c.lastModifiedDateTime) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="activeTab === 'app-protection'" class="tab-content">
        <div v-if="data.appProtection.length === 0" class="empty-block">No app protection policies configured.</div>
        <table v-else class="data-table">
          <thead><tr><th>Policy</th><th>Platform</th><th>Created</th><th>Modified</th></tr></thead>
          <tbody>
            <tr v-for="p in data.appProtection" :key="p.policyId" class="cursor-pointer" @click="openDetail('App Protection', p)">
              <td class="cell-name">{{ p.displayName }}</td>
              <td>{{ p.platform || '--' }}</td>
              <td class="date-cell">{{ formatDate(p.createdDateTime) }}</td>
              <td class="date-cell">{{ formatDate(p.lastModifiedDateTime) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="showDrawer" class="drawer-overlay" @click.self="closeDrawer">
      <div class="drawer">
        <div class="drawer-header">
          <div class="drawer-title-group">
            <span class="drawer-type-badge">{{ drawerType }}</span>
            <h2>{{ drawerItem.displayName }}</h2>
          </div>
          <button class="drawer-close" @click="closeDrawer">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="20" height="20">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="drawer-body">
          <div class="drawer-meta-grid">
            <div class="meta-item">
              <span class="meta-label">Platform</span>
              <span class="meta-value">{{ drawerItem.platform || '--' }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Created</span>
              <span class="meta-value">{{ formatDate(drawerItem.createdDateTime) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Modified</span>
              <span class="meta-value">{{ formatDate(drawerItem.lastModifiedDateTime) }}</span>
            </div>
          </div>
          <div class="drawer-section">
            <div class="section-label">Policy Configuration</div>
            <pre class="json-block">{{ JSON.stringify(drawerItem.raw, null, 2) }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import { getEntraIntune, getEntraTenantSummary, getEntraTenantCapabilities } from '@/services/entra-api.js';
import { getCapabilityStateLabel } from '@/services/entra-utils.js';

export default {
  name: 'EntraIntune',
  components: { EntraPageHeader },
  data: function() {
    return {
      tenantId: '', tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '',
      freshnessState: 'fresh', lastCollection: '', dataCompleteness: null, capabilities: [],
      activeTab: 'devices',
      tabs: [
        { key: 'devices', label: 'Devices' },
        { key: 'compliance', label: 'Compliance Policies' },
        { key: 'configs', label: 'Device Configs' },
        { key: 'app-protection', label: 'App Protection' }
      ],
      data: { devices: [], compliancePolicies: [], deviceConfigs: [], appProtection: [] },
      tableState: 'loading',
      error: '',
      showDrawer: false, drawerType: '', drawerItem: {}
    };
  },
  methods: {
    formatDate: function(ts) {
      if (!ts) return '--';
      try { return new Date(ts).toLocaleDateString(); } catch(e) { return '--'; }
    },
    tabCount: function(key) {
      if (key === 'devices') return this.data.devices.length;
      if (key === 'compliance') return this.data.compliancePolicies.length;
      if (key === 'configs') return this.data.deviceConfigs.length;
      if (key === 'app-protection') return this.data.appProtection.length;
      return 0;
    },
    loadData: function() {
      var self = this;
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) { this.error = 'Please select a tenant first.'; return; }
      getEntraTenantSummary(this.tenantId).then(function(data) {
        var t = data.tenant || data;
        self.tenantName = t.displayName || ''; self.primaryDomain = t.primaryDomain || '';
        self.regionCode = t.regionCode || ''; self.tenantStatus = t.status || '';
        self.lastCollection = t.lastSuccessfulCollectionAt || '';
      }).catch(function() {});
      getEntraTenantCapabilities(this.tenantId).then(function(data) {
        self.capabilities = (data || []).map(function(c) { return { key: c.capabilityKey, state: c.state, stateLabel: getCapabilityStateLabel(c.state) }; });
      }).catch(function() {});

      getEntraIntune(this.tenantId).then(function(d) {
        self.data = d;
        self.tableState = 'loaded';
        if (d.devices.length > 0) self.activeTab = 'devices';
        else if (d.compliancePolicies.length > 0) self.activeTab = 'compliance';
        else if (d.deviceConfigs.length > 0) self.activeTab = 'configs';
        else if (d.appProtection.length > 0) self.activeTab = 'app-protection';
      }).catch(function(err) { self.error = err.message; });
    },
    openDetail: function(type, item) {
      this.drawerType = type;
      this.drawerItem = item;
      this.showDrawer = true;
    },
    closeDrawer: function() {
      this.showDrawer = false;
      this.drawerItem = {};
    }
  },
  mounted: function() { this.loadData(); },
  watch: { '$route.params.tenantId': 'loadData' }
};
</script>

<style scoped>
.entra-page { min-height: 100%; background: #F0F9FF; }
.page-body { padding: 20px 24px; }

.error-banner { padding: 12px 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; color: #991b1b; font-size: 0.8125rem; margin-bottom: 16px; }

.tabs-bar { display: flex; gap: 4px; margin-bottom: 16px; border-bottom: 2px solid #e2e8f0; }
.tab-btn {
  padding: 8px 16px; border: none; background: none;
  font-size: 0.8125rem; font-weight: 500; color: #64748b;
  cursor: pointer; border-bottom: 2px solid transparent;
  margin-bottom: -2px; transition: color 0.15s, border-color 0.15s;
  font-family: 'Inter', sans-serif; display: inline-flex; align-items: center; gap: 6px;
}
.tab-btn:hover { color: #0C4A6E; }
.tab-active { color: #0369A1; border-bottom-color: #0369A1; font-weight: 600; }
.tab-count { font-size: 0.6875rem; background: #f1f5f9; padding: 1px 7px; border-radius: 8px; color: #64748b; }

.loading-block { padding: 48px; text-align: center; color: #94a3b8; }
.empty-block { padding: 48px; text-align: center; color: #94a3b8; font-size: 0.875rem; }

.tab-content { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }

.data-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.data-table th { background: #f8fafc; color: #475569; font-weight: 600; text-align: left; padding: 10px 14px; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
.data-table td { padding: 9px 14px; border-bottom: 1px solid #f1f5f9; color: #334155; }
.data-table tbody tr:hover { background: #F0F9FF; }

.cell-name { font-weight: 600; color: #0C4A6E; }
.date-cell { font-size: 0.75rem; color: #64748b; white-space: nowrap; }

.pill { font-size: 0.6875rem; font-weight: 600; padding: 2px 8px; border-radius: 4px; text-transform: capitalize; }
.pill-ok { background: #dcfce7; color: #047857; }
.pill-warn { background: #fef3c7; color: #92400e; }

.cursor-pointer { cursor: pointer; }

.drawer-overlay { position: fixed; top: 0; right: 0; bottom: 0; left: 0; background: rgba(12,74,110,0.4); z-index: 100; display: flex; justify-content: flex-end; backdrop-filter: blur(2px); }
.drawer { width: 640px; max-width: 92vw; height: 100%; background: #ffffff; overflow-y: auto; box-shadow: -12px 0 40px rgba(3,105,161,0.12); animation: drawer-slide 0.22s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes drawer-slide { from { transform: translateX(40px); opacity: 0.7; } to { transform: translateX(0); opacity: 1; } }
.drawer-header { display: flex; justify-content: space-between; align-items: flex-start; padding: 16px 20px; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0; background: rgba(255,255,255,0.95); backdrop-filter: blur(8px); z-index: 1; }
.drawer-title-group { display: flex; flex-direction: column; gap: 6px; }
.drawer-type-badge { font-size: 0.625rem; font-weight: 700; color: #0369A1; background: #dbeafe; padding: 2px 8px; border-radius: 4px; align-self: flex-start; text-transform: uppercase; }
.drawer-header h2 { margin: 0; font-size: 1rem; font-weight: 600; color: #0C4A6E; word-break: break-word; }
.drawer-close { background: none; border: none; color: #94a3b8; cursor: pointer; padding: 6px; border-radius: 8px; }
.drawer-close:hover { color: #475569; background: #f1f5f9; }
.drawer-body { padding: 20px; }
.drawer-meta-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid #f1f5f9; }
.meta-item { display: flex; flex-direction: column; gap: 3px; }
.meta-label { font-size: 0.625rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; }
.meta-value { font-size: 0.8125rem; color: #0C4A6E; }
.drawer-section { margin-bottom: 16px; }
.section-label { font-size: 0.75rem; font-weight: 700; color: #0369A1; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 8px; }
.json-block { background: #F0F9FF; border: 1px solid #bae6fd; border-radius: 8px; padding: 14px; font-size: 0.6875rem; color: #0C4A6E; overflow-x: auto; white-space: pre-wrap; font-family: 'SF Mono', 'Fira Code', monospace; line-height: 1.5; }

</style>
